import React from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { z } from 'zod';

import { normalizeEmail } from '@clearverify/utils';

import {
  type EmployeeInviteEmailOptionInfoFragment,
  useGetEmployeeInviteEmailOptionsForAdminQuery,
  useGetEmployeeInviteEmailOptionsForEmployerScopeQuery,
} from 'src/api';
import { REQUIRED_VALIDATION_MESSAGE } from 'src/constants/validationMessage';
import type { EmployeeInviteLinkArgs, ResendInviteScope } from 'src/hooks/employee';
import { useSendEmployeeInvite } from 'src/hooks/employee/useSendEmployeeInvite';

const MANUAL_EMAIL_OPTION = '__manual__';

const manualEmailSchema = z.email({ error: 'Enter a valid email address' }).nonempty({
  error: REQUIRED_VALIDATION_MESSAGE,
});

type SendEmployeeInviteModalContentProps = {
  employeeId: string;
  employeeSsnEncrypted: string;
  inviteEmailOptionsScope: 'admin' | 'employerScope';
  sendInviteScope: ResendInviteScope;
  inviteLinkArgs?: EmployeeInviteLinkArgs | null;
  onInviteSent?: () => void;
  onCloseModal: () => void;
  switchClosability: (closability: boolean) => void;
};

const formatEmailOptionLabel = (option: EmployeeInviteEmailOptionInfoFragment) => {
  if (option.employerDisplayNames.length === 0) return option.email;

  return `${option.email} (${option.employerDisplayNames.join(', ')})`;
};

export const SendEmployeeInviteModalContent: React.FC<SendEmployeeInviteModalContentProps> = ({
  employeeId,
  employeeSsnEncrypted,
  inviteEmailOptionsScope,
  sendInviteScope,
  inviteLinkArgs,
  onInviteSent,
  onCloseModal,
  switchClosability,
}) => {
  const sendEmployeeInvite = useSendEmployeeInvite(sendInviteScope, {
    successMessage: 'Invite sent',
  });

  const isAdminScope = inviteEmailOptionsScope === 'admin';
  const adminQuery = useGetEmployeeInviteEmailOptionsForAdminQuery({
    variables: { employeeSsnEncrypted },
    skip: !isAdminScope,
    fetchPolicy: 'network-only',
  });
  const employerScopeQuery = useGetEmployeeInviteEmailOptionsForEmployerScopeQuery({
    variables: { employeeSsnEncrypted },
    skip: isAdminScope,
    fetchPolicy: 'network-only',
  });

  const emailOptions = isAdminScope
    ? adminQuery.data?.getEmployeeInviteEmailOptionsForAdmin
    : employerScopeQuery.data?.getEmployeeInviteEmailOptionsForEmployerScope;
  const loadingOptions = isAdminScope ? adminQuery.loading : employerScopeQuery.loading;
  const optionsError = isAdminScope ? adminQuery.error : employerScopeQuery.error;

  const [selectedOption, setSelectedOption] = React.useState<string | null>(null);
  const [manualEmail, setManualEmail] = React.useState('');
  const [manualEmailError, setManualEmailError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!emailOptions || selectedOption) return;
    if (emailOptions.length > 0) {
      const firstOption = emailOptions[0];
      if (firstOption) {
        setSelectedOption(firstOption.email);
      }
      return;
    }
    setSelectedOption(MANUAL_EMAIL_OPTION);
  }, [emailOptions, selectedOption]);

  React.useEffect(() => {
    if (loadingOptions || selectedOption) return;
    if (optionsError || !emailOptions) {
      setSelectedOption(MANUAL_EMAIL_OPTION);
    }
  }, [emailOptions, loadingOptions, optionsError, selectedOption]);

  // Only the email is sent to the server; the invitee's name is resolved
  // server-side from MDM so it can't be spoofed by the client.
  const resolveInviteEmail = React.useCallback((): string | null => {
    if (selectedOption === MANUAL_EMAIL_OPTION) {
      const parsed = manualEmailSchema.safeParse(manualEmail.trim());
      if (!parsed.success) {
        setManualEmailError(parsed.error.issues[0]?.message ?? REQUIRED_VALIDATION_MESSAGE);
        return null;
      }
      setManualEmailError(null);
      return normalizeEmail(parsed.data);
    }

    const selectedEmailOption = emailOptions?.find(option => option.email === selectedOption);
    if (!selectedEmailOption) return null;

    return normalizeEmail(selectedEmailOption.email);
  }, [emailOptions, manualEmail, selectedOption]);

  const handleSubmit = React.useCallback(async () => {
    const inviteEmail = resolveInviteEmail();
    if (!inviteEmail) return;

    switchClosability(false);
    setSubmitting(true);
    try {
      await sendEmployeeInvite(employeeId, inviteLinkArgs, inviteEmail);
      onInviteSent?.();
      onCloseModal();
    } catch {
      // Errors surface via the notification ErrorLink.
    } finally {
      setSubmitting(false);
      switchClosability(true);
    }
  }, [
    employeeId,
    inviteLinkArgs,
    onCloseModal,
    onInviteSent,
    resolveInviteEmail,
    sendEmployeeInvite,
    switchClosability,
  ]);

  return (
    <Stack spacing={2}>
      <Typography variant="body1" color="text.secondary">
        Choose the email address that should receive the Clear Verify signup invite.
      </Typography>

      {loadingOptions ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <FormControl>
          <RadioGroup
            value={selectedOption ?? ''}
            onChange={event => {
              setSelectedOption(event.target.value);
              setManualEmailError(null);
            }}
          >
            {(emailOptions ?? []).map(option => (
              <FormControlLabel
                key={option.email}
                value={option.email}
                control={<Radio />}
                label={formatEmailOptionLabel(option)}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      {optionsError && !loadingOptions && (
        <Typography variant="body2" color="warning.main">
          Couldn't load suggested emails, enter one manually.
        </Typography>
      )}

      <Divider />

      <FormControl>
        <RadioGroup
          value={selectedOption ?? ''}
          onChange={event => {
            setSelectedOption(event.target.value);
            setManualEmailError(null);
          }}
        >
          <FormControlLabel
            value={MANUAL_EMAIL_OPTION}
            control={<Radio />}
            label="Enter email manually"
          />
        </RadioGroup>
      </FormControl>

      {selectedOption === MANUAL_EMAIL_OPTION && (
        <TextField
          label="Email address"
          type="email"
          value={manualEmail}
          onChange={event => {
            setManualEmail(event.target.value);
            setManualEmailError(null);
          }}
          error={Boolean(manualEmailError)}
          helperText={manualEmailError}
          fullWidth
          autoFocus
        />
      )}

      <Box display="flex" alignItems="center" justifyContent="center" gap={2} pt={1}>
        <Button
          variant="outlined"
          color="secondary"
          size="large"
          onClick={onCloseModal}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={() => void handleSubmit()}
          loading={submitting}
          disabled={loadingOptions || submitting || !selectedOption}
        >
          Invite
        </Button>
      </Box>
    </Stack>
  );
};
